import { Check, Infinity as InfinityIcon } from "lucide-react";
import { featureLabel, featureValueText, orderedCategories, rollUpWidgetTiers, widgetTierLabel } from "@wa/ui";
import { makeT } from "@wa/ui/plans-i18n";
import { UNLIMITED, type EntitlementDef, type EntitlementMap } from "../../../lib/billing";

type T = (k: string, v?: Record<string, string | number>) => string;

/**
 * Category caption, with a fallback that cannot print a raw key path.
 *
 * The account catalogue (`billing.cat.*`) is preferred because it is what runtime locales
 * get machine-translated into. It is also a hand-written mirror of the API's category list
 * and WILL drift again, and `t()` returns the key path itself on a miss - so a new API
 * category would render the literal text "billing.cat.whatever" on screen. The shared
 * @wa/ui catalogue covers every category the comparison table knows, so it takes over
 * whenever the account file has no entry.
 */
function categoryLabel(category: string, t: T, loc: "et" | "en"): string {
  const key = `billing.cat.${category}`;
  const own = t(key);
  if (own !== key) return own;
  const shared = makeT(loc)(`cat.${category}`);
  return shared === `cat.${category}` ? category : shared;
}

/**
 * The entitlement list behind the plan card's disclosure.
 *
 * `defs` arrives pre-filtered to either the granted or the not-granted half, so the same
 * component renders both panels and neither has to repeat the other's rows. Category order
 * comes from `orderedCategories`, NOT from a local list: this file used to carry a
 * hand-written CATEGORY_ORDER that was missing `builder`, which silently dropped all
 * fifteen of its rows. `orderedCategories` appends unknown categories instead, so the worst
 * case is a block in the wrong place rather than a block nobody sees.
 */
export function EntitlementList({
  defs, features, locale, t, muted = false,
}: {
  defs: EntitlementDef[];
  features: EntitlementMap;
  locale: string;
  t: T;
  /** The "not included" panel: labels only, dimmed, no value column. */
  muted?: boolean;
}) {
  const loc = locale === "et" ? "et" : "en";
  return (
    <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
      {orderedCategories(defs).map((category) => {
        const categoryDefs = defs.filter((d) => d.category === category);
        if (categoryDefs.length === 0) return null;
        // Same roll-up as the plan cards and the comparison table: fourteen exclusive
        // widgets become one line per tier. A customer buys "the flagship widgets", not
        // "the cinema widget", and fifteen widget names would drown the block.
        const rows = rollUpWidgetTiers(categoryDefs);
        return (
          <div key={category}>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              {categoryLabel(category, t, loc)}
            </p>
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.key} className="flex items-center justify-between gap-3 text-sm">
                  <span className={muted ? "min-w-0 truncate text-muted-foreground/60" : "min-w-0 truncate text-muted-foreground"}>
                    {row.tier ? widgetTierLabel(row.tier, row.tierKeys!.length, loc) : featureLabel(row.def!.key, loc)}
                  </span>
                  {!muted && <GrantedValue def={row.def} value={row.def ? features[row.def.key] : undefined} t={t} locale={loc} />}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/** Value cell of a granted row. A rolled-up widget tier has no single value, so it gets the
 *  same tick a granted boolean gets. */
function GrantedValue({
  def, value, t, locale,
}: {
  def: EntitlementDef | undefined;
  value: boolean | number | undefined;
  t: T;
  locale: "et" | "en";
}) {
  if (!def || def.type === "boolean") return <Check className="size-4 shrink-0 text-success" />;
  if (value === UNLIMITED) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 font-medium text-foreground">
        <InfinityIcon className="size-3.5" /> {t("billing.unlimited")}
      </span>
    );
  }
  return (
    <span className="shrink-0 font-medium tabular-nums text-foreground">
      {featureValueText(def.key, value, locale)}
    </span>
  );
}
